import {
    replicateRxCollection,
    type RxReplicationState
} from 'rxdb/plugins/replication';
import type {
    RxReplicationPullStreamItem,
    RxCollection,
    WithDeleted
} from 'rxdb';
import { Subject, type Subscription } from 'rxjs';
import { z } from 'zod';
import type { Checkpoint, PullResponse } from '@ast-data/shared';
import { entityConfigs } from '@ast-data/shared';
import type { AppDatabase } from './database';
import { getToken, authFetch, fetchAs } from '../utils/authFetch';
import {
    credentialFor,
    credentialIdentity,
    invalidateCredential,
    dropCredential
} from '../utils/pendingCredentials';
import {
    authorsFor,
    clearAuthors,
    hasPendingChanges,
    pendingCountsByAuthor,
    pendingLocalChanges,
    getCurrentAuthor
} from './authorJournal';
import {
    blockSync,
    clearSyncBlocker,
    getSyncBlockers,
    SyncBlockedError
} from './syncBlockers';
import {
    recordSyncError,
    clearSyncErrors,
    markSyncFailing
} from '../features/sync/syncErrorLog';
import { recordRejections } from '../features/sync/pushRejections';
import { rememberSentStates } from '../features/sync/sentStates';
import { reportStreamEpoch } from '../utils/dataEpoch';
import { runPendingRepairs } from './repairs';
import { pruneOutdatedStores } from './pruneOutdatedStores';
import { listPendingIds } from '../features/media/blobStore';
import { createLimiter } from './pushLimiter';

export const replicationStates: RxReplicationState<unknown, unknown>[] = [];

let authActive = true;

/**
 * Push requests in flight at once, over ALL collections. RxDB would send every
 * batch of a large import at the same moment (see pushLimiter); a handful at a
 * time keeps the server answering the other devices meanwhile. Five: each push
 * holds one of the server's ten database connections for its transaction, so
 * one importing device leaves half the pool to everybody else. Two devices
 * importing at once would fill it — the 10 s connection wait then pushes back.
 */
const MAX_PARALLEL_PUSHES = 5;
/**
 * How long one push may hold its slot. A slower one keeps running — it is
 * never aborted — but lets the next one start, so a request that never
 * answers cannot stall the whole queue.
 */
const PUSH_SLOT_LEASE_MS = 60_000;
const limitPush = createLimiter(MAX_PARALLEL_PUSHES, PUSH_SLOT_LEASE_MS);
/**
 * A batch this small is somebody's edit, not an import: it skips the queue,
 * so a saved Tagebuch does not wait minutes behind an import's batches.
 */
const SMALL_PUSH = 5;

/**
 * Which tab syncs — RxDB's own two rules: `waitForLeadership` (one leader per
 * database) and `toggleOnDocumentVisible` (a visible tab replicates too, a
 * hidden non-leader pauses). Everything around the replication must ask
 * isReplicatingHere() rather than model this itself.
 */

let replicationDb: AppDatabase | null = null;
/** Whether a replication of THIS session has ever started in this tab. */
let startedHere = false;
const presenceListeners = new Set<() => void>();

function isLeaderTab(): boolean {
    try {
        return replicationDb?.isLeader() ?? false;
    } catch {
        // No leader election (no Web Locks): the visibility half still holds.
        return false;
    }
}

function isDocumentVisible(): boolean {
    // No document at all: the tests, in Node.
    return (
        typeof document === 'undefined' ||
        document.visibilityState === 'visible'
    );
}

/**
 * Whether this tab is syncing right now: `startedHere` (from `startPromise`)
 * plus the pause RxDB applies to every hidden non-leader.
 */
export function isReplicatingHere(): boolean {
    if (!startedHere) {
        return false;
    }
    return isLeaderTab() || isDocumentVisible();
}

export function subscribeReplicationPresence(listener: () => void): () => void {
    presenceListeners.add(listener);
    return () => void presenceListeners.delete(listener);
}

/** Take note that this tab replicates. Exported for the tests (no election). */
export function setReplicatingHere(value: boolean): void {
    startedHere = value;
    updateReplicationPresence();
}

/**
 * The shared stream belongs to the tab that replicates — otherwise its frames
 * land in subjects nobody listens to, for a permanent connection.
 */
function updateReplicationPresence(): void {
    if (authActive) {
        const syncing = isReplicatingHere();
        if (syncing && !sharedEventSource) {
            connectSharedSSE();
        } else if (!syncing && sharedEventSource) {
            closeSharedSSE();
        }
    }
    for (const listener of presenceListeners) {
        listener();
    }
}

/** The `error$` taps below, so a re-login does not leave the old ones running. */
const errorSubscriptions: Subscription[] = [];

/**
 * One multiplexed SSE connection for every collection: browsers cap HTTP/1.1 at
 * ~6 per origin, and one EventSource each starved pull/push. Every event names
 * its collection, which routes it to the right Subject.
 */
let sharedEventSource: EventSource | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
const streamSubjects = new Map<
    string,
    Subject<RxReplicationPullStreamItem<unknown, Checkpoint>>
>();

export function setReplicationAuthActive(active: boolean) {
    authActive = active;
    if (!active) {
        closeSharedSSE();
        // Cursors of a running re-pull must not outlive their replications.
        cancelFullResync();
        for (const rep of replicationStates) {
            rep.cancel().catch(() => {});
        }
    }
}

/**
 * Whether the live stream is up — for the sync report: a device that pushes and
 * pulls fine but lost the stream sees changes late and cannot tell.
 */
export function isStreamConnected(): boolean {
    return sharedEventSource !== null;
}

function closeSharedSSE() {
    sharedEventSource?.close();
    sharedEventSource = null;
    if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
    }
}

const RESYNC_COALESCE_MS = 2000;
let lastResyncAt = 0;

/**
 * Ask every replication to catch up from its stored checkpoint.
 *
 * THE STREAM ALONE IS NOT ENOUGH: every SSE frame moves the persisted
 * checkpoint, so a document written while this device had no stream is skipped
 * by every later pull and stays invisible here for good, with no error anywhere.
 * Not a re-download — one pull per collection, normally empty. Coalesced.
 */
export function resyncAllReplications() {
    if (!authActive || !isReplicatingHere()) {
        // Not syncing here: `reSync()` would go into a stream nobody started.
        return;
    }
    const now = Date.now();
    if (now - lastResyncAt < RESYNC_COALESCE_MS) {
        return;
    }
    lastResyncAt = now;
    for (const rep of replicationStates) {
        rep.reSync();
    }
}

// --- Full re-pull ("Daten neu laden") ---------------------------------------

/**
 * The collections walking their whole table, and how far each has got. No value
 * means "from the beginning"; the key being present at all is what makes the
 * pull handler ignore RxDB's checkpoint.
 */
const fullPullCursors = new Map<string, Checkpoint | undefined>();

let fullResyncTotal = 0;
let fullResyncListener: ((done: number, total: number) => void) | null = null;
/** Resolves the promise `startFullResync` hands out; see there. */
let finishFullResync: (() => void) | null = null;
/** Waiting on a walk of SOME collections — see `startCollectionWalk`. */
const walkWaiters = new Set<() => void>();
/** Bumped whenever a running walk is cut short (cancel, logout, user switch). */
let walkGeneration = 0;

function notifyFullResyncProgress() {
    for (const waiter of [...walkWaiters]) {
        waiter();
    }
    if (!fullResyncListener) {
        return;
    }
    const done = fullResyncTotal - fullPullCursors.size;
    fullResyncListener(done, fullResyncTotal);
}

export function isFullResyncRunning(): boolean {
    return fullPullCursors.size > 0;
}

/**
 * Pull EVERY document of every collection again, from the first row — the repair
 * for a device missing something for good: what was lost is BEHIND the stored
 * checkpoint (see resyncAllReplications), where no catch-up reaches it.
 *
 * NOT `RxReplicationState.remove()`, which drops the UPSTREAM checkpoint too and
 * would re-push every local document. Local documents are not deleted first;
 * each pulled row goes through the conflict handler like any other pull.
 *
 * `onProgress` reports finished COLLECTIONS: the client cannot know the rows.
 */
export async function startFullResync(
    onProgress?: (done: number, total: number) => void
): Promise<void> {
    if (!authActive) {
        throw new AuthError();
    }
    if (!isReplicatingHere()) {
        // Not the syncing tab: nothing would walk, and the progress would sit
        // at 0 of 16 for ever. Better to say which window can do it.
        throw new NotReplicatingError();
    }
    for (const rep of replicationStates) {
        fullPullCursors.set(rep.collection.name, undefined);
    }
    fullResyncTotal = fullPullCursors.size;

    // Resolved by the walk itself, as each collection drops its cursor. NOT
    // `awaitInSync()`: that waits on the push side too, which never goes idle on
    // a device holding a colleague's queued offline edits.
    const walked = new Promise<void>((resolve) => {
        finishFullResync = resolve;
    });
    fullResyncListener = (done, total) => {
        onProgress?.(done, total);
        if (fullPullCursors.size === 0) {
            finishFullResync?.();
        }
    };
    notifyFullResyncProgress();

    try {
        // One resync drives the whole walk: RxDB keeps pulling while the pages
        // come back full, which they do until the end.
        for (const rep of replicationStates) {
            rep.reSync();
        }
        await walked;
    } finally {
        // Cursors still standing mean the walk was cut short: clear them, and
        // bump the generation so a repair riding along is not counted as done.
        if (fullPullCursors.size > 0) {
            walkGeneration += 1;
        }
        fullPullCursors.clear();
        fullResyncListener = null;
        finishFullResync = null;
        notifyFullResyncProgress();
    }
}

/**
 * Read SOME collections again from the first row — {@link startFullResync}'s
 * walk, aimed at what a repair concerns (see db/repairs.ts). A catch-up cannot
 * help a document that is WRONG rather than missing: the server row has not
 * changed, so no pull carries it. Rejects if replication is not running here,
 * so the repair stays owed.
 */
export async function startCollectionWalk(names: string[]): Promise<void> {
    if (!authActive) {
        throw new AuthError();
    }
    if (!isReplicatingHere()) {
        throw new NotReplicatingError();
    }
    const walking = replicationStates
        .map((rep) => rep.collection.name)
        .filter((name) => names.includes(name));
    // "Walked nothing" must NOT come back as "done" — that would silently burn
    // the repair on every device.
    const unknown = names.filter((name) => !walking.includes(name));
    if (unknown.length > 0) {
        throw new Error(`Unknown collection(s): ${unknown.join(', ')}`);
    }
    for (const name of walking) {
        fullPullCursors.set(name, undefined);
    }
    // A cancelled walk drops its cursors too, which is all the waiter can see —
    // the generation tells the two apart (see runPendingRepairs).
    const generation = walkGeneration;
    const walked = new Promise<void>((resolve, reject) => {
        const check = () => {
            if (walking.every((name) => !fullPullCursors.has(name))) {
                walkWaiters.delete(check);
                if (generation === walkGeneration) {
                    resolve();
                } else {
                    reject(new Error('Walk interrupted'));
                }
            }
        };
        walkWaiters.add(check);
    });
    for (const rep of replicationStates) {
        if (walking.includes(rep.collection.name)) {
            rep.reSync();
        }
    }
    await walked;
    // The cursor drops when the last page ARRIVES, a moment before RxDB has
    // written it — so wait for the database to go quiet before reporting done.
    await replicationDb?.requestIdlePromise();
}

/**
 * Give up on a running full re-pull. What was pulled stays; the checkpoint may
 * sit behind the head, which the next catch-up moves forward.
 */
export function cancelFullResync() {
    walkGeneration += 1;
    fullPullCursors.clear();
    finishFullResync?.();
    notifyFullResyncProgress();
}

let catchUpListenersInstalled = false;

/**
 * Catch up whenever the device comes back: `online` and `visibilitychange` both
 * fire while the EventSource still believes it is connected, so `onopen` is not
 * enough. Installed once; the resync is a no-op while paused.
 */
function installCatchUpListeners() {
    if (catchUpListenersInstalled || typeof window === 'undefined') {
        return;
    }
    catchUpListenersInstalled = true;
    window.addEventListener('online', () => resyncAllReplications());
    document.addEventListener('visibilitychange', () => {
        // RxDB toggles this tab's replications on the very same event
        // (toggleOnDocumentVisible), so the stream follows them.
        updateReplicationPresence();
        if (document.visibilityState === 'visible') {
            resyncAllReplications();
        }
    });
}

function connectSharedSSE() {
    if (!authActive) {
        return;
    }
    const token = getToken();
    if (!token) {
        return;
    }

    const es = new EventSource(
        `/api/stream?token=${encodeURIComponent(token)}`
    );
    sharedEventSource = es;

    // Every connect is a catch-up (see resyncAllReplications). Here and not in
    // `onerror`, because a stream can come back without this client ever seeing
    // the error.
    es.onopen = () => resyncAllReplications();

    es.onmessage = (event) => {
        try {
            const data = JSON.parse(event.data) as {
                collection: string;
                documents: ({ _deleted?: boolean } & Record<string, unknown>)[];
                checkpoint: Checkpoint;
            };
            const subject = streamSubjects.get(data.collection);
            if (!subject) {
                return;
            }
            subject.next({
                documents: data.documents.map((doc) => ({
                    ...doc,
                    _deleted: doc._deleted ?? false
                })),
                checkpoint: data.checkpoint
            });
        } catch {
            // ignore parse errors
        }
    };

    // The server names the body of data it serves on every connect, which is how
    // a tablet nobody reloads finds out its copy is stale (see utils/dataEpoch).
    es.addEventListener('meta', (event) => {
        try {
            const data = JSON.parse((event as MessageEvent).data) as {
                dataEpoch?: unknown;
            };
            if (typeof data.dataEpoch === 'string' && data.dataEpoch !== '') {
                reportStreamEpoch(data.dataEpoch);
            }
        } catch {
            // ignore parse errors
        }
    });

    es.onerror = () => {
        es.close();
        if (sharedEventSource === es) {
            sharedEventSource = null;
        }
        if (authActive) {
            reconnectTimer = setTimeout(connectSharedSSE, 5000);
        }
    };
}

/**
 * A failed request as an error someone can act on: the body carries the reason
 * (a validation message, a Prisma failure, a proxy's HTML page) and this is the
 * only moment it exists.
 */
async function httpError(what: string, response: Response): Promise<Error> {
    let body = '';
    try {
        body = (await response.text()).slice(0, 500);
    } catch {
        // A body that cannot be read is not worth a second failure.
    }
    const error = new Error(
        `${what} failed: ${response.status} ${response.statusText}${body ? ` – ${body}` : ''}`
    );
    (error as Error & { status?: number }).status = response.status;
    return error;
}

/**
 * The shape of a pull page, checked before anything is done with it.
 *
 * The DOCUMENTS are deliberately not inspected: a full Zod pass over a
 * 100-document page more than triples the parse (0.69 ms against 0.30 ms). The
 * ENVELOPE is free (0.0006 ms) and is where the damage would be permanent —
 * `checkpoint` becomes this collection's cursor, and one that is quietly wrong
 * skips whatever lies behind it, for good.
 */
const pullPageSchema = z.object({
    documents: z.array(z.unknown()),
    // ISO-8601 from this server; older builds persisted epoch millis, which the
    // server still accepts. This guard is about the cursor BEING a cursor.
    checkpoint: z
        .object({
            id: z.string(),
            updatedAt: z.union([z.string(), z.number()])
        })
        .nullable()
});

class AuthError extends Error {
    constructor() {
        super('Unauthorized');
    }
}

export class NotReplicatingError extends Error {
    constructor() {
        super('This tab does not replicate');
    }
}

/** One row as RxDB hands it to the push handler. */
interface PushRow {
    newDocumentState: Record<string, unknown>;
    assumedMasterState?: Record<string, unknown>;
}

/**
 * Rows already accepted upstream during a batch that could not finish: a blocked
 * author makes the push throw, and RxDB retries the very same batch every
 * `retryTime`. Keyed by content, so a retry carries only genuinely new work.
 *
 * collection name → document id → fingerprint of the accepted state
 */
const acceptedRows = new Map<string, Map<string, string>>();

function fingerprint(row: PushRow): string {
    return JSON.stringify(row.newDocumentState);
}

function rememberAccepted(
    collectionName: string,
    rows: PushRow[],
    idOf: (row: PushRow) => string
): void {
    let accepted = acceptedRows.get(collectionName);
    if (!accepted) {
        accepted = new Map();
        acceptedRows.set(collectionName, accepted);
    }
    for (const row of rows) {
        accepted.set(idOf(row), fingerprint(row));
    }
}

async function postPush(
    apiBase: string,
    token: string | null,
    rows: PushRow[],
    schemaVersion: number
): Promise<Response> {
    return fetchAs(token, `${apiBase}/push`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            // The shape these documents are in. A server further on lifts
            // them to its own before storing (see upgradeDocument).
            schemaVersion,
            docs: rows.map((row) => ({
                newDocumentState: row.newDocumentState,
                assumedMasterState: row.assumedMasterState
            }))
        })
    });
}

/**
 * Send a push batch split by who actually made each change.
 *
 * A device that changed hands offline carries the previous user's edits, which
 * go up with the token kept for them so the server credits the right author.
 * Everything else goes up as the signed-in user.
 *
 * When that token is gone their rows are held back and the person is listed in
 * `syncBlockers`; everyone else's still go up. Stalling is deliberate: a held
 * queue is visible and fixable, a mis-credited record is neither.
 */
export async function pushByAuthor<T>(
    apiBase: string,
    collectionName: string,
    primaryPath: string,
    rows: PushRow[],
    schemaVersion: number
): Promise<WithDeleted<T>[]> {
    const idOf = (row: PushRow) => String(row.newDocumentState[primaryPath]);

    // Drop what an earlier, unfinished run of this batch already got through.
    const accepted = acceptedRows.get(collectionName);
    const pending = accepted
        ? rows.filter((row) => accepted.get(idOf(row)) !== fingerprint(row))
        : rows;

    const authors = authorsFor(collectionName, pending.map(idOf));

    // Group by author; '' is the signed-in user (and anything unjournalled).
    const groups = new Map<string, PushRow[]>();
    for (const row of pending) {
        const author = authors.get(idOf(row)) ?? '';
        const group = groups.get(author);
        if (group) {
            group.push(row);
        } else {
            groups.set(author, [row]);
        }
    }

    const conflicts: WithDeleted<T>[] = [];
    const pushed: string[] = [];
    const blocked: string[] = [];

    // Only a *previous* user's rows need a credential kept for them; everything
    // else goes up with the session token.
    const signedIn = getCurrentAuthor();
    for (const [author, group] of groups) {
        const foreign = author !== '' && author !== signedIn;
        const token = foreign ? credentialFor(author) : getToken();
        if (foreign && !token) {
            // Nothing proves this is them — hold their documents back rather
            // than file their work under whoever is holding the device.
            recordBlocker(author, group.length);
            blocked.push(author);
            continue;
        }

        rememberSentStates(collectionName, primaryPath, group);
        const response = await postPush(apiBase, token, group, schemaVersion);

        if (response.status === 401 && foreign) {
            // Their token died between the check and the request.
            invalidateCredential(author);
            recordBlocker(author, group.length);
            blocked.push(author);
            continue;
        }

        if (response.status === 401) {
            setReplicationAuthActive(false);
            throw new AuthError();
        }
        if (!response.ok) {
            throw await httpError('Push', response);
        }

        const rejected = (await response.json().catch(() => null)) as
            WithDeleted<T>[] | null;
        if (!Array.isArray(rejected)) {
            // Without a conflict list we cannot tell what landed. Throwing keeps
            // the whole batch in the journal rather than marking it delivered.
            throw new Error(
                `Push failed: ${collectionName} answered with something other than a conflict list`
            );
        }
        conflicts.push(...rejected);

        // A conflicted document stays in the journal for another round.
        const conflicted = new Set(
            rejected.map((doc) =>
                String((doc as Record<string, unknown>)[primaryPath])
            )
        );
        const landed = group.filter((row) => !conflicted.has(idOf(row)));
        pushed.push(...landed.map(idOf));
        rememberAccepted(collectionName, landed, idOf);
        // The only moment a rejected write is visible: it is resolved silently,
        // and afterwards the local document IS the server's.
        recordRejections(collectionName, [...conflicted]);
    }

    // `authors` is what the journal said BEFORE this batch went out; anything
    // written again since stays put (see clearAuthors).
    clearAuthors(collectionName, pushed, authors);
    // Only now is the journal current, so this is where a drained user's token
    // can be let go — checking inside the loop above would read stale counts.
    for (const author of new Set(authors.values())) {
        if (!hasPendingChanges(author)) {
            dropCredential(author);
            clearSyncBlocker(author);
        }
    }

    if (blocked.length === 0) {
        // The batch got through; RxDB advances past it.
        acceptedRows.delete(collectionName);
    } else {
        // Throwing is what keeps RxDB from considering the held rows synced —
        // the push contract has no "skip this row".
        throw new SyncBlockedError(blocked);
    }
    return conflicts;
}

/** Publish who is holding up the queue, with their total across collections. */
function recordBlocker(userId: string, countInThisBatch: number): void {
    const identity = credentialIdentity(userId);
    blockSync({
        userId,
        username: identity?.username ?? '',
        name: identity?.name ?? userId,
        // The journal knows the real total; the batch only sees its own slice.
        count: pendingCountsByAuthor().get(userId) ?? countInThisBatch
    });
}

/**
 * One collection's pull: everything after a checkpoint — RxDB's, or this
 * collection's OWN cursor during a full re-pull (see startFullResync). The walk
 * ends on a SHORT PAGE, the same rule RxDB stops its pull loop by, so the last
 * checkpoint is the head and ordinary streaming resumes.
 */
export function createPullHandler<T>(apiBase: string, collectionName: string) {
    return async (
        lastCheckpoint: Checkpoint | undefined,
        batchSize: number
    ): Promise<PullResponse<T & { _deleted: boolean }>> => {
        if (!authActive) throw new AuthError();

        const rebuilding = fullPullCursors.has(collectionName);
        const from = rebuilding
            ? fullPullCursors.get(collectionName)
            : lastCheckpoint;

        const params = new URLSearchParams();
        if (from) {
            params.set('id', from.id);
            params.set('updatedAt', String(from.updatedAt));
        }
        params.set('limit', String(batchSize));

        const response = await authFetch(`${apiBase}/pull?${params}`);

        if (response.status === 401) {
            setReplicationAuthActive(false);
            throw new AuthError();
        }

        if (!response.ok) {
            throw await httpError('Pull', response);
        }

        const page = pullPageSchema.safeParse(
            await response.json().catch(() => null)
        );
        if (!page.success) {
            // Throwing leaves the checkpoint untouched and hands this to RxDB's
            // retry — the right answer to a proxy answering for the server.
            const fields = [
                ...new Set(
                    page.error.issues.map((issue) => issue.path.join('.'))
                )
            ].join(', ');
            throw new Error(
                `Pull failed: ${collectionName} answered with an unusable page (${fields})`
            );
        }
        const data = page.data as PullResponse<T & { _deleted: boolean }>;

        if (rebuilding) {
            if (data.documents.length < batchSize) {
                fullPullCursors.delete(collectionName);
                notifyFullResyncProgress();
            } else {
                fullPullCursors.set(collectionName, data.checkpoint);
            }
        }
        return data;
    };
}

function setupCollectionReplication<T>(
    collection: RxCollection<T>,
    apiBase: string,
    identifier: string,
    collectionName: string,
    readOnly = false
) {
    // Keyed by the server-side collection name, so the shared SSE can route to it.
    const pullStream$ = new Subject<
        RxReplicationPullStreamItem<T, Checkpoint>
    >();
    streamSubjects.set(
        collectionName,
        pullStream$ as unknown as Subject<
            RxReplicationPullStreamItem<unknown, Checkpoint>
        >
    );

    // The session this replication belongs to — see setupGeneration.
    const generation = setupGeneration;
    const replication = replicateRxCollection({
        collection,
        replicationIdentifier: identifier,
        deletedField: '_deleted',
        live: true,
        retryTime: 5000,

        // Keep the tab that is ON SCREEN syncing, so the device never waits on a
        // leader the browser has hibernated — on phones a hidden tab is frozen
        // outright and RxDB's mousemove keep-alive cannot wake it. The price:
        // two tabs push at once, see features/sync/sentStates.ts.
        toggleOnDocumentVisible: true,

        // Pull-only entities (e.g. users, sourced from the Synology NAS) leave
        // push undefined so local changes are never sent upstream.
        push: readOnly
            ? undefined
            : {
                  batchSize: 50,
                  handler: async (docs) => {
                      const push = () => {
                          /**
                           * Checked when the push RUNS, not when it queued: a
                           * batch that waited through a logout or a user switch
                           * must not go out under whoever is signed in now.
                           */
                          if (!authActive || generation !== setupGeneration) {
                              throw new AuthError();
                          }
                          return pushByAuthor<T>(
                              apiBase,
                              collectionName,
                              collection.schema.primaryPath as string,
                              docs as PushRow[],
                              collection.schema.version
                          );
                      };
                      return await (docs.length <= SMALL_PUSH
                          ? push()
                          : limitPush(push));
                  }
              },

        pull: {
            batchSize: 100,
            handler: createPullHandler<T>(apiBase, collectionName),
            stream$: pullStream$.asObservable()
        }
    });

    // `startPromise` is the one signal for "this tab now syncs". The generation
    // keeps a promise from an ended session from reporting for the new one.
    void replication.startPromise.then(() => {
        if (generation === setupGeneration) {
            setReplicatingHere(true);
        }
    });

    // Here and not in the status hook: once per collection, not once per chip.
    errorSubscriptions.push(
        replication.error$.subscribe((error) => {
            // Offline nothing counts: the retry is bound to fail.
            if (!recordSyncError(collectionName, error)) {
                return;
            }
            // A held queue is a task, not a fault — do not paint it red.
            markSyncFailing(getSyncBlockers().length === 0);
        }),
        // A document that went up says the sync works again.
        replication.sent$.subscribe(() => markSyncFailing(false))
    );

    replicationStates.push(replication as RxReplicationState<unknown, unknown>);
    return replication;
}

/**
 * Which call to `setupReplication` we are in: a login, a logout and a user
 * switch each start over, and a promise from an ended session must not report
 * back for the new one.
 */
let setupGeneration = 0;

export function setupReplication(db: AppDatabase) {
    setupGeneration += 1;
    // Drop the cursors of the session that just ended, and tell its waiters.
    walkGeneration += 1;
    fullPullCursors.clear();
    notifyFullResyncProgress();
    replicationDb = db;
    startedHere = false;
    for (const rep of replicationStates) {
        rep.cancel().catch(() => {});
    }
    replicationStates.length = 0;
    for (const subscription of errorSubscriptions) {
        subscription.unsubscribe();
    }
    errorSubscriptions.length = 0;
    // Reasons belong to the session that hit them; a fresh login starts clean.
    clearSyncErrors();
    closeSharedSSE();
    streamSubjects.clear();
    authActive = true;

    const collections = db as unknown as Record<string, RxCollection<unknown>>;
    for (const entity of entityConfigs) {
        setupCollectionReplication(
            collections[entity.collection],
            entity.apiBase,
            `${entity.collection}-rest-replication`,
            entity.collection,
            entity.readOnly ?? false
        );
    }

    installCatchUpListeners();
    // Closes a stream left over from the session before.
    updateReplicationPresence();

    // What this device still owes from an older build's bug, once replication is
    // going HERE and only for this session. `allSettled`: one collection failing
    // to start must not skip the repairs of the others.

    const session = setupGeneration;
    void Promise.allSettled(
        replicationStates.map((rep) => rep.startPromise)
    ).then(() => {
        if (session !== setupGeneration) {
            return;
        }
        void runPendingRepairs(startCollectionWalk);
        /**
         * And the leftovers of past migrations, while we are here. AFTER the
         * replication is up on purpose: during the open, deleting databases is
         * the last thing this app should be doing (see db/openStall.ts), and a
         * device that has caught up is also the one whose old stores are
         * provably finished with.
         */
        void pruneOutdatedStores(
            async () => {
                const photos = await listPendingIds().catch(() => ['unknown']);
                return pendingLocalChanges() > 0 || photos.length > 0;
            },
            db.name,
            // The checkpoint stores in use right now, by the name RxDB's Dexie
            // storage gives them; every other one is a leftover.
            async () =>
                new Set(
                    await Promise.all(
                        replicationStates.map(async (rep) => {
                            const meta = await rep.metaInfoPromise;
                            return `rxdb-dexie-${db.name}--${rep.collection.schema.version}--${meta.collectionName}`;
                        })
                    )
                )
        ).then((removed) => {
            if (removed.length > 0) {
                console.info(
                    `[RxDB] ${removed.length} alte Datenbank(en) entfernt`
                );
            }
        });
    });
}
